import { chatService } from "@/_services";

// Il badge delle conversazioni stava dentro ogni navbar, con un setInterval per
// conto suo. Da lì venivano tre difetti, che chi guardava vedeva tutti e tre
// come «il numero non è aggiornato»:
//
// 1. la risposta in ritardo sovrascriveva quella fresca. Aprendo la
//    conversazione il badge andava a zero, ma la richiesta partita un attimo
//    prima tornava col numero vecchio e lo rimetteva su. Ora ogni richiesta
//    porta un numero d'ordine e le risposte scadute si buttano;
//
// 2. il sondaggio dell'operatore era subordinato al dato che avrebbe dovuto
//    aggiornare: senza conversazioni la voce non compare, e senza voce il
//    sondaggio usciva subito - quindi la prima conversazione non arrivava mai,
//    e ci voleva un ricaricamento della pagina;
//
// 3. setInterval in una scheda in secondo piano viene rallentato dal browser a
//    un giro al minuto, e nelle schede congelate si ferma del tutto. Al rientro
//    il badge restava vecchio, ed è esattamente la prova a due finestre.
//
// Il conteggio sta qui e non nella navbar perché non è della navbar: lo cambiano
// anche la scheda paziente e il back-office della struttura, che aprono la stessa
// modale senza che la navbar li senta.

const ATTESA = 15000;

// Ogni quanto vale la pena richiedere l'elenco dei canali invece del solo
// conteggio. L'elenco è la sola risposta che dice se le conversazioni esistono,
// e serve a far comparire la voce a chi la prima conversazione se l'è vista
// abilitare mentre era già dentro. Costa più del conteggio, quindi non va sul
// giro: si rifà tornando sulla scheda, e non più spesso di così.
const RICARICO_CANALI = 60000;

let timer = null;
let progressivo = 0;
let ultimoCaricoCanali = 0;
let ascoltatore = null;

// Il conteggio vive nello store, che al cambio di utenza resta in piedi mentre la
// navbar viene distrutta e ricostruita. Due cose devono quindi saper riconoscere
// il giro a cui appartengono, altrimenti sopravvivono a chi le ha chieste: una
// risposta ancora in volo, che dipingerebbe sulla navbar nuova il numero di chi
// era collegato prima; e il `finally` di quella richiesta, che riprogrammerebbe
// il giro dopo che è stato fermato.
let attivo = false;
let epoca = 0;

function inSecondoPiano() {
  return typeof document !== "undefined" && document.hidden;
}

function fermaTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

const state = {
  nonLetti: 0,
  haCanali: false,
  // Numero d'ordine dell'ultima risposta applicata: quelle più vecchie non
  // devono poter tornare indietro sopra una più recente
  ordineApplicato: 0,
};

const actions = {
  // Elenco dei canali: dice sia il conteggio sia se le conversazioni esistono
  caricaCanali({ commit }) {
    const ordine = ++progressivo;
    const mia = epoca;
    ultimoCaricoCanali = Date.now();

    return chatService
      .getMyChannels()
      .then((response) => {
        const canali = response?.data?.data?.data || response?.data?.data || [];
        const totale = canali.reduce(
          (somma, canale) => somma + Number(canale.unread_count || 0),
          0
        );
        commit("setStato", {
          ordine,
          epoca: mia,
          nonLetti: totale,
          haCanali: canali.length > 0,
        });
        return totale;
      })
      .catch(() => 0);
  },

  // Solo il conteggio: due query, ed è quello che gira sul ritmo
  aggiorna({ commit }) {
    const ordine = ++progressivo;
    const mia = epoca;

    return chatService
      .getUnreadCount()
      .then((response) => {
        const nonLetti = Number(response?.data?.data?.count || 0);
        commit("setStato", { ordine, epoca: mia, nonLetti });
        return nonLetti;
      })
      .catch(() => 0);
  },

  avvia({ dispatch }) {
    attivo = true;
    dispatch("caricaCanali");
    dispatch("programma");

    if (typeof document !== "undefined" && !ascoltatore) {
      ascoltatore = () => {
        if (inSecondoPiano()) {
          return;
        }
        // Tornando sulla scheda si guarda subito, invece di aspettare il giro:
        // è il momento in cui il numero vecchio si nota
        if (Date.now() - ultimoCaricoCanali > RICARICO_CANALI) {
          dispatch("caricaCanali");
        } else {
          dispatch("aggiorna");
        }
        dispatch("programma");
      };
      document.addEventListener("visibilitychange", ascoltatore);
    }
  },

  // setTimeout concatenato invece di setInterval: se una risposta tarda, il giro
  // dopo parte quando quella è arrivata, e le richieste non si accavallano
  programma({ dispatch }) {
    fermaTimer();
    if (!attivo) {
      return;
    }
    timer = setTimeout(() => {
      if (inSecondoPiano()) {
        // Scheda nascosta: non si chiede niente, ma il giro resta in piedi.
        // Al rientro ci pensa l'ascoltatore, subito
        dispatch("programma");
        return;
      }
      dispatch("aggiorna").finally(() => dispatch("programma"));
    }, ATTESA);
  },

  ferma({ commit }) {
    attivo = false;
    epoca += 1;
    fermaTimer();
    if (typeof document !== "undefined" && ascoltatore) {
      document.removeEventListener("visibilitychange", ascoltatore);
      ascoltatore = null;
    }
    // Al cambio di utenza il numero di prima non deve restare appeso
    commit("azzera");
  },
};

const mutations = {
  setStato(state, { ordine, epoca: epocaRisposta, nonLetti, haCanali }) {
    // Risposta di un giro chiuso: la navbar che l'aveva chiesta non c'e' piu'
    if (epocaRisposta !== epoca) {
      return;
    }
    // La risposta di una richiesta partita prima non riporta indietro il badge
    if (ordine < state.ordineApplicato) {
      return;
    }
    state.ordineApplicato = ordine;
    state.nonLetti = nonLetti;

    if (typeof haCanali === "boolean") {
      state.haCanali = haCanali;
    } else if (nonLetti > 0) {
      // Un messaggio non letto implica una conversazione: la voce compare senza
      // aspettare l'elenco
      state.haCanali = true;
    }
  },
  azzera(state) {
    state.nonLetti = 0;
    state.haCanali = false;
    state.ordineApplicato = 0;
    ultimoCaricoCanali = 0;
    // `progressivo` non si azzera: e' il numero d'ordine, e deve solo crescere
  },
};

const getters = {
  nonLetti: (state) => state.nonLetti,
  haCanali: (state) => state.haCanali,
};

export const chat = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
